// IndexedDB Client using Dexie
// Singleton database instance for offline-first data persistence

import Dexie, { type EntityTable } from 'dexie';
import type { Broker } from '@/domains/route/types/Broker';
import type { Route } from '@/domains/route/types/Route';
import type { RouteStop } from '@/domains/routeStop/types/RouteStop';
import type { Shipment } from '@/domains/shipment/types/Shipment';
import type { Address } from '@/domains/address';
import type { SyncJob } from '@/services/sync/syncService';
import type { ConstantCacheEntry } from '@/domains/constants/types';
import type { DeviceImageRecord } from '@/domains/deviceImages/types/deviceImagesType';

class HPLiteDatabase extends Dexie {
  // Define tables using domain types
  brokers!: EntityTable<Broker, 'id'>;
  routes!: EntityTable<Route, 'id'>;
  routeStops!: EntityTable<RouteStop, 'id'>;
  shipments!: EntityTable<Shipment, 'id'>;
  addresses!: EntityTable<Address, 'id'>;
  syncQueue!: EntityTable<SyncJob, 'id'>;
  constants!: EntityTable<ConstantCacheEntry, 'typeName'>;
  deviceImages!: EntityTable<DeviceImageRecord, 'id'>;

  constructor() {
    super('HPLiteDB');

    // v1 schema kept for safe upgrade path from existing installs.
    this.version(1).stores({
      brokers: 'id, firstName, lastName, emailAddress, companyName, validationCode',
      routes:
        'id, code, routeDate, routeDateLocal, statusCodeId, inputTypeId, startedOn, syncedOn, syncStatus, [code+routeDate]',
      routeStops: 'id, routeId, addressId',
      shipments:
        'id, trackingNumber, routeId, destinationAddressId, scannedAddressId, statusCodeId, shipmentTypeId, customerId, [routeId+trackingNumber]',
      addresses: 'id',
      constants: 'typeName',
      deviceImagesQueue:
        '++id, entityId, deviceImageTypeId, syncStatus, [deviceImageTypeId+entityId]',
      deviceImages:
        '++id, entityId, deviceImageTypeId, timestamp, cacheKey, syncStatus, retryCount, routeId, routeStopId, shipmentGuid, [deviceImageTypeId+entityId], [syncStatus], [routeId], [routeStopId], [shipmentGuid]',

      // Offline sync queue
      syncQueue:
        'id, type, status, createdAt, syncedAt, retryCount, newRecord, oldRecord, error, [type+status], [status]',
    });

    // Current schema: remove the unused deviceImagesQueue table.
    this.version(2).stores({
      brokers: 'id, firstName, lastName, emailAddress, companyName, validationCode',
      routes:
        'id, code, routeDate, routeDateLocal, statusCodeId, inputTypeId, startedOn, syncedOn, syncStatus, [code+routeDate]',
      routeStops: 'id, routeId, addressId',
      shipments:
        'id, trackingNumber, routeId, destinationAddressId, scannedAddressId, statusCodeId, shipmentTypeId, customerId, [routeId+trackingNumber]',
      addresses: 'id',
      constants: 'typeName',
      deviceImages:
        '++id, entityId, deviceImageTypeId, timestamp, cacheKey, syncStatus, retryCount, routeId, routeStopId, shipmentGuid, [deviceImageTypeId+entityId], [syncStatus], [routeId], [routeStopId], [shipmentGuid]',

      // Offline sync queue
      syncQueue:
        'id, type, status, createdAt, syncedAt, retryCount, newRecord, oldRecord, error, [type+status], [status]',
    });
  }
}

export const db = new HPLiteDatabase();
